import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get } from "./api";
import { SEVERITY_CLASS, STATUS_LABEL } from "./api";
import type { ActionStatus, TrackedItem } from "./api";

/**
 * Tracked items, with the filters that go with them.
 *
 * One component for both places it appears — the cross-customer page and a
 * customer's own tab — so the two cannot drift into sorting differently or
 * disagreeing about what "open" shows. `customer` is the only difference
 * between them: pinned to a slug, the customer column drops out, because
 * repeating one name down a column says nothing.
 */
export default function TrackedList({
  customer,
  showClosed,
  mine,
  onToggleClosed,
  onToggleMine,
}: {
  customer?: string;
  showClosed: boolean;
  mine: boolean;
  onToggleClosed: () => void;
  onToggleMine: () => void;
}) {
  const [items, setItems] = useState<TrackedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (customer) q.set("customer", customer);
    if (showClosed) q.set("include_closed", "true");
    if (mine) q.set("assignee", "me");
    setItems(null);
    void get<{ items: TrackedItem[] }>(`/api/actions?${q}`)
      .then((d) => setItems(d.items))
      .catch((e) => setError(e.message));
  }, [customer, showClosed, mine]);

  useEffect(load, [load]);

  if (error) return <div className="error">{error}</div>;

  return (
    <>
      <div className="picker">
        <label className="check">
          <input type="checkbox" checked={showClosed} onChange={onToggleClosed} />
          Show resolved and cancelled
        </label>
        <label className="check">
          <input type="checkbox" checked={mine} onChange={onToggleMine} />
          Assigned to me
        </label>
      </div>

      {!items ? (
        <div className="empty">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty">
          {showClosed || mine
            ? "Nothing matches those filters."
            : customer
              ? "Nothing tracked for this customer yet — track one from the report."
              : "Nothing tracked yet. Open a customer's report and track a suggestion."}
        </div>
      ) : (
        <table style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>Severity</th>
              <th>Status</th>
              {!customer && <th>Customer</th>}
              <th>Item</th>
              <th>Assignee</th>
              <th>ETA</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className={i.is_open ? "" : "off"}>
                <td>
                  <span className={`badge ${SEVERITY_CLASS[i.severity]}`}>{i.severity}</span>
                </td>
                <td>
                  <StatusPill status={i.status} />
                </td>
                {!customer && (
                  <td>
                    <Link to={`/c/${i.customer}`}>{i.customer_display_name}</Link>
                  </td>
                )}
                <td>
                  <Link to={`/actions/${i.id}`}>
                    <strong>{i.title}</strong>
                  </Link>
                  {i.comment_count > 0 && (
                    <span className="chip tiny" style={{ marginLeft: 8 }}>
                      {i.comment_count} comment{i.comment_count === 1 ? "" : "s"}
                    </span>
                  )}
                  {i.description && <div className="dim">{i.description}</div>}
                </td>
                <td>{i.assignee_name ?? "—"}</td>
                {/* A calendar date, printed as stored: converting it to a
                    local timezone would shift it by a day for anybody west
                    of UTC and mean nothing. */}
                <td>{i.eta ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function StatusPill({ status }: { status: ActionStatus }) {
  return <span className={`pill st-${status}`}>{STATUS_LABEL[status]}</span>;
}

/**
 * The two filter toggles, as URL state.
 *
 * In the query string rather than in component state so a filtered list is
 * a link somebody can send — and so switching tabs and coming back does not
 * silently reset what you were looking at.
 */
export function useListFilters(params: URLSearchParams, set: (p: URLSearchParams) => void) {
  const showClosed = params.get("closed") === "1";
  const mine = params.get("mine") === "1";
  const toggle = (k: string, on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.delete(k);
    else next.set(k, "1");
    set(next);
  };
  return {
    showClosed,
    mine,
    onToggleClosed: () => toggle("closed", showClosed),
    onToggleMine: () => toggle("mine", mine),
  };
}
